import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { normalizeKycDetails } from "../../pages/users/kycUtils";
import type { UserWalletRecord } from "../../pages/users/userActivityWalletUtils";
import type { UserBalanceSummary } from "../../pages/users/userBalanceSummaryUtils";
import {
  normalizeUserTransactionsResponse,
  USER_ACTIVITY_TX_PAGE_SIZE,
  type UserTransactionsPageResponse,
} from "../../pages/users/userActivityTransactionUtils";
import { useAuth } from "../../context/auth-context";
import axiosInstance from "../../utils/axiosConfig";
import { compactParams } from "./params";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";

type Envelope<T> = { data?: T };

export type { UserWalletRecord };
export type { UserBalanceSummary } from "../../pages/users/userBalanceSummaryUtils";

export type AdminUserDetails = {
  user?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  wallets?: UserWalletRecord[];
};

export function useAdminUserDetailsQuery(customerId: string) {
  const { isAuthenticated } = useAuth();

  return useApiQuery<Envelope<AdminUserDetails>, AdminUserDetails | null>({
    queryKey: apiQueryKey("admin-user-details", customerId),
    url: `api/users/${customerId}`,
    enabled: Boolean(isAuthenticated && customerId),
    select: (res) => res?.data ?? null,
  });
}

export type UserTransactionStatusFilter =
  | ""
  | "COMPLETED"
  | "PENDING"
  | "FAILED";

export function useAdminUserTransactionsPageQuery(
  customerId: string,
  page: number,
  status: UserTransactionStatusFilter = ""
) {
  const { isAuthenticated } = useAuth();

  return useQuery<UserTransactionsPageResponse, AxiosError>({
    queryKey: apiQueryKey(
      "admin-user-transactions-page",
      customerId,
      page,
      status
    ),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<Envelope<unknown>>(
        `api/admin/users/${customerId}/transactions`,
        {
          params: compactParams({
            page,
            limit: USER_ACTIVITY_TX_PAGE_SIZE,
            status: status || undefined,
          }),
          signal,
        }
      );
      return normalizeUserTransactionsResponse(data?.data);
    },
    enabled: Boolean(isAuthenticated && customerId),
    placeholderData: (previousData) => previousData,
  });
}

export type UserActivityLog = {
  title?: string;
  content?: string;
  createdAt?: string;
  readAt?: string | null;
};

export function useAdminUserActivitiesQuery(
  customerId: string,
  enabled = true
) {
  const { isAuthenticated } = useAuth();

  return useQuery<UserActivityLog[], AxiosError>({
    queryKey: apiQueryKey("admin-user-activities", customerId),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<
        Envelope<{ activityLogs?: UserActivityLog[] }>
      >(`api/users/${customerId}/activities`, { signal });
      const logs = data?.data?.activityLogs;
      return Array.isArray(logs) ? logs : [];
    },
    enabled: Boolean(isAuthenticated && customerId && enabled),
  });
}

export type UserReward = {
  _id?: string;
  createdAt?: string;
  source?: string;
  reason?: string;
  message?: string;
  points?: number;
};

export function useAdminUserRewardsQuery(customerId: string, enabled = true) {
  const { isAuthenticated } = useAuth();

  return useQuery<UserReward[], AxiosError>({
    queryKey: apiQueryKey("admin-user-rewards", customerId),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<
        Envelope<{ rewards?: UserReward[] }>
      >(`api/tasks/rewards/${customerId}`, { signal });
      const rewards = data?.data?.rewards;
      return Array.isArray(rewards) ? rewards : [];
    },
    enabled: Boolean(isAuthenticated && customerId && enabled),
  });
}

export type UserTask = {
  _id?: string;
  applied?: boolean;
  applicationStatus?: string;
  points?: number;
  name?: string;
  description?: string;
  [key: string]: unknown;
};

export function useAdminUserTasksQuery(customerId: string, enabled = true) {
  const { isAuthenticated } = useAuth();

  return useQuery<UserTask[], AxiosError>({
    queryKey: apiQueryKey("admin-user-tasks", customerId),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<
        Envelope<{ tasks?: UserTask[] }>
      >(`api/tasks/${customerId}`, { signal });
      const tasks = data?.data?.tasks;
      return Array.isArray(tasks) ? tasks : [];
    },
    enabled: Boolean(isAuthenticated && customerId && enabled),
  });
}

export type AdminUserKycDetails = ReturnType<typeof normalizeKycDetails> & {
  userId: { _id: string; [key: string]: unknown };
};

export function useAdminUserKycDetailsQuery(
  customerId: string,
  enabled = true
) {
  const { isAuthenticated } = useAuth();

  return useQuery<AdminUserKycDetails, AxiosError>({
    queryKey: apiQueryKey("admin-user-kyc-details", customerId),
    queryFn: async ({ signal }) => {
      const kycRes = await axiosInstance.get<
        Envelope<{ kycDetails?: unknown }>
      >(`api/users/me/kyc/user/${customerId}`, { signal });
      const base = normalizeKycDetails(
        kycRes.data?.data?.kycDetails,
        customerId
      );
      try {
        const userRes = await axiosInstance.get<Envelope<AdminUserDetails>>(
          `api/users/${customerId}`,
          { signal }
        );
        const user = userRes.data?.data?.user;
        return {
          ...base,
          userId: user ? { _id: customerId, ...user } : base.userId,
        } as AdminUserKycDetails;
      } catch {
        return base as AdminUserKycDetails;
      }
    },
    enabled: Boolean(isAuthenticated && customerId && enabled),
  });
}

export function useAdminUserBalanceSummaryQuery(customerId: string) {
  const { isAuthenticated } = useAuth();

  return useApiQuery<Envelope<UserBalanceSummary>, UserBalanceSummary | null>({
    queryKey: apiQueryKey("admin-user-balance-summary", customerId),
    url: `api/admin/users/${customerId}/balance-summary`,
    enabled: Boolean(isAuthenticated && customerId),
    select: (res) => res?.data ?? null,
  });
}
